import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import en from "./locales/en.json";
import pt from "./locales/pt.json";
import es from "./locales/es.json";
import fr from "./locales/fr.json";
import zh from "./locales/zh.json";

import type { LanguageCode } from "../store/auth";

const resources = {
  en: { translation: en },
  pt: { translation: pt },
  es: { translation: es },
  fr: { translation: fr },
  zh: { translation: zh },
};

export const supportedLanguages = [
  { code: "en" as const, name: "English", flag: "🇺🇸", short: "EN" },
  { code: "pt" as const, name: "Português", flag: "🇧🇷", short: "BR" },
  { code: "es" as const, name: "Español", flag: "🇪🇸", short: "ES" },
  { code: "fr" as const, name: "Français", flag: "🇫🇷", short: "FR" },
  { code: "zh" as const, name: "中文", flag: "🇨🇳", short: "CN" },
];

export const SUPPORTED_LANGUAGE_CODES: LanguageCode[] = ["en", "pt", "es", "fr", "zh"];

/**
 * Determines the language to use based on priority:
 * User preference > Company default > Browser > English
 */
export function determineLanguage(
  userPreference: LanguageCode | null | undefined,
  companyDefault: LanguageCode | null | undefined
): LanguageCode {
  // Priority 1: User preference
  if (userPreference && SUPPORTED_LANGUAGE_CODES.includes(userPreference)) {
    return userPreference;
  }

  // Priority 2: Company default
  if (companyDefault && SUPPORTED_LANGUAGE_CODES.includes(companyDefault)) {
    return companyDefault;
  }

  // Priority 3: Browser language
  const browserLang = navigator.language.split("-")[0] as LanguageCode;
  if (SUPPORTED_LANGUAGE_CODES.includes(browserLang)) {
    return browserLang;
  }

  // Priority 4: Fallback to English
  return "en";
}

/**
 * Initializes the language based on user and company preferences.
 * Call this after login or when user data is loaded.
 */
export function initializeLanguage(
  userPreference: LanguageCode | null | undefined,
  companyDefault: LanguageCode | null | undefined
): void {
  const language = determineLanguage(userPreference, companyDefault);
  i18n.changeLanguage(language);
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: "en",
    supportedLngs: ["en", "pt", "es", "fr", "zh"],
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ["navigator"],
      caches: [],
    },
  });

export default i18n;
